/** Modulos necesarios del contenedor */
import React, { useContext, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
	GTIWebTextField, GTIWebButton, CircularProgress, Paper, Grid,
	CheckCircle, Error, GTIWebAlert, Stack, Box, DoNotDisturbAlt,
	Link
} from "@gti-controls-v2/web-core";
import { AuthContext } from "../../providers/auth";
import { LocaleContext } from "../../providers/locale";
import { useTranslation } from "../../hooks/useTranslation";
import { paths } from "../../common/constants/Rutas";
import loginStyles from "../../common/styles/Login";
import useInputValidator from "../../hooks/useInputValidator";
import LoginLayout from "../../layouts/Login/LoginLayout";
import customTheme from "../../theme";
import apiConfig from "../../config/api";
import { tipoSistema } from "../../common/constants/Tipos";
import { useMediaQueryCustom } from "../../hooks/useMediaQueryCustom/useMediaQueryCustom";
import logo from "../../assets/images/logo-masorden-gris-small.svg";

/** Constante del contenedor */
export const Login = () => {

	/** Hooks  */
	const styles = loginStyles;
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const { login } = useContext(AuthContext);
	const { changeLocale } = useContext(LocaleContext);
	const { validateInput: inputValidator } = useInputValidator();
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const { isMobile480, isTablet768, isTablet1024 } = useMediaQueryCustom();

	/** Asigna el Meta Title fijo a la página */
	// document.title = `${t("sistema.login.titulos.iniciarSesion")} ${t("sistema.titulos.aplicacion")}`;

	/** Estado de la forma */
	const [loginData, setLoginData] = useState({
		username: searchParams.get("correo") || "",
		password: "",
		userState: null,
		passwordState: null,
		userLegend: t("sistema.login.titulos.emailLabel"),
		userIcon: null,
		passwordLegend: null,
	});

	/** Acceso a APP */
	const onLogin = async (e) => {
		try {
			setIsLoading(true);
			if (e) e.preventDefault();
			if (loginData.username !== "" && loginData.password !== "") {

				// Codigo pora hacer login
				const loginResponse = await fetchLogin();
				const loginData = loginResponse.data;

				if (loginResponse.ok) {
					changeLocale("es-MX");

					const token_expires_at = new Date();
					token_expires_at.setSeconds(token_expires_at.getSeconds() + loginData?.token.expires_in);

					const data = {
						token: loginData?.token.access_token,
						refresh_token: loginData?.token.refresh_token,
						token_expires_in: loginData?.token.expires_in,
						token_expires_at: token_expires_at,
						usuarioNombre: loginData?.user.UsuarioNombre,
						usuarioID: loginData?.user.UsuarioID,
						sessionID: loginData?.user.SessionID,
						usuarioEmail: loginData?.user.email,
						roles: loginData?.user.roles,
					};

					login(data);

					navigate(paths.HomePage, { replace: true });

				}
				else {
					if (loginResponse.status === 400) {
						setError({ message: loginData.error_description });
					}
					else {
						setError({ message: (`${t("sistema.errores.fetchError")} (${loginResponse.status})`) });
					}
				}

			}
		} catch (err) {
			console.error('Error al realizar login:', err);
			setError({ message: t("sistema.errores.fetchError") });
		}
		finally {
			setIsLoading(false);
		}

	};

	/** Función que hace la petición al servidor para obtener el token de acceso */
	const fetchLogin = async () => {
		let payload = {
			username: loginData.username,
			password: loginData.password,
			type: tipoSistema.ConsolaSoporte
		};

		const responseLogin = await fetch(apiConfig.API_URL_AUTH, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		});

		const responseData = await responseLogin.json();

		let response =
		{
			"ok": responseLogin.ok,
			"status": responseLogin.status,
			"message": responseLogin.statusText,
			"data": {
				"error_description": ""
			}
		};

		if (response.ok) {
			response.data = {
				"user": {
					"SessionID": responseData.user.sessionId,
					"UsuarioID": responseData.user.userId,
					"UsuarioNombre": responseData.user.name,
					"email": responseData.user.email,
					"roles": responseData.roles,
				},
				"token": {
					"refresh_token": responseData.cognito.AuthenticationResult.RefreshToken,
					"access_token": responseData.cognito.AuthenticationResult.AccessToken,
					"expires_in": responseData.cognito.AuthenticationResult.ExpiresIn,
					"tokenType": responseData.cognito.AuthenticationResult.TokenType
				},
			};
		} else {
			response.data.error_description = responseData.msg
		}

		return (response);
	}

	/** Función que se ejecuta cuando se cambia el input de username y password */
	const handleChange = (e) => {
		setLoginData({ ...loginData, [e.target.name]: e.target.value.trim() });
	};

	/** Valida el input de usuario, si es valido se muestra el icono correcto, si no se muestra el icono de error */
	const validateInput = () => {
		const handleValidator = (objectResponse) => {
			if (objectResponse.isValid) {
				setLoginData({
					...loginData,
					userLegend: t("sistema.login.titulos.emailLegend"),
					userState: "success",
					userIcon: (
						<CheckCircle style={{ color: customTheme.paleta.success40 }} />
					),
				});
			} else {
				setLoginData({
					...loginData,
					userLegend: objectResponse.text,
					userState: "error",
					userIcon: <Error style={{ color: customTheme.paleta.error40 }} />,
				});
			}
		};
		inputValidator({ text: loginData.username }, handleValidator);
	};

	const handleOnForgot = () => {
		navigate(paths.RecuperarPassword);
	}

	/** UI */
	return (
		<LoginLayout>
			<Grid id="login-grid-form" item component={Paper} sx={styles.paperContainer}>
				<form id="login-form" onSubmit={onLogin} style={styles.form} >
					<Stack
						sx={styles.form} direction="column" spacing={isMobile480 ? 2 : 1}
						padding={isMobile480 ? "16px 24px 16px 24px" : (isTablet768 || isTablet1024) ? "24px 24px 24px 24px" : "40px 24px 40px 24px"}
						style={{ textAlign: "-webkit-left" }}
						alignItems="center"
					>
						<Box sx={styles.logoMoContainer}>
							<Box
								component="img"
								src={logo}
								alt="login"
								sx={styles.logoMO}
							/>
						</Box>

						<Grid container>
							<Box sx={styles.title}>{t("sistema.login.titulos.iniciarSesion")}</Box>
						</Grid>

						{error && (
							<GTIWebAlert
								icon={<DoNotDisturbAlt />}
								severity="error"
								title={error.message}
								sx={{ maxWidth: "442px" }}
							/>
						)}

						<GTIWebTextField
							name="username"
							value={loginData.username}
							onChange={handleChange}
							labelText={t("sistema.login.titulos.emailLabel")}
							placeholder={t("sistema.login.titulos.emailPlaceholder")}
							width="100%"
							disabled={isLoading}
							autoFocus={true}
							icon={loginData.userIcon}
							onBlur={validateInput}
							onFocus={validateInput}
							state={loginData.userState}
							autoComplete="email"
							legendText="Entra con el dato que tienes registrado"
						/>

						<GTIWebTextField
							name="password"
							disabled={isLoading}
							value={loginData.password}
							onChange={handleChange}
							labelText={t("sistema.login.titulos.passwordLabel")}
							placeholder={t("sistema.login.titulos.passwordPlaceholder")}
							type="password"
							legendText={loginData.passwordLegend}
							width="100%"
							state={loginData.passwordState}
							autoComplete="off"
						/>
						<Grid container sx={styles.forgotPassword}>
							<Link
								disabled={isLoading}
								variant="body2"
								component={"button"}
								type={"button"}
								onClick={(e) => handleOnForgot()}
							>
								{t("sistema.login.acciones.forgotPassword")}
							</Link>
						</Grid>

						<GTIWebButton
							onlyIcon={isLoading}
							icon={isLoading && <CircularProgress size={30} />}
							disabled={isLoading}
							iconPosition="end"
							type="submit"
							text={t("sistema.login.acciones.boton")}
						/>

					</Stack>
				</form>
			</Grid>
		</LoginLayout>
	);
};

/** Exportacion del componente para su invocacion en otros archivos */
export default Login;